import { createApi, FetchBaseQueryMeta } from '@reduxjs/toolkit/query/react';

import {
  FreeReport,
  FreeReportFilter,
  MortalityReport,
  MortalityReportFilter,
  TaskIndividualReportPayload,
  TaskReport,
  TaskReportFilter,
} from '@/pages/Report/types';
import { PageableRequest } from '@/shared/api/types';
import {
  AUTOPSY_ACT_DOWNLOAD,
  FREE_REPORT,
  FREE_REPORT_DOWNLOAD,
  MORTALITY_REPORT,
  MORTALITY_REPORT_DOWNLOAD,
  TASK_CONTROL_REPORT,
  TASK_INDIV_ARCHIVED_DOWNLOAD,
  TASK_INDIV_DOWNLOAD,
  TASK_REPORT,
  TASK_REPORT_DOWNLOAD,
} from '@/shared/report/api';
import { mapSortToQueryParameter } from '@/utils/utils';

import { baseQueryWithReAuth } from '../baseApi';
import { reportTags } from './tags';

export const reportApi = createApi({
  reducerPath: 'reportApi',
  baseQuery: baseQueryWithReAuth,
  tagTypes: Object.values(reportTags),
  endpoints: (builder) => ({
    getTaskReport: builder.query<TaskReport[], PageableRequest<TaskReportFilter>>({
      query: (payload) => ({
        url: TASK_REPORT,
        method: 'GET',
        params: {
          ...payload?.filters,
          sort: mapSortToQueryParameter(payload?.sort),
        },
      }),
      providesTags: [reportTags.TASK],
    }),
    downloadTaskReport: builder.query<
      { data: Blob; meta: FetchBaseQueryMeta },
      PageableRequest<TaskReportFilter>
    >({
      query: (payload) => ({
        url: TASK_REPORT_DOWNLOAD,
        method: 'GET',
        params: {
          ...payload?.filters,
          sort: mapSortToQueryParameter(payload?.sort),
        },
        responseHandler: (response) => response.blob(),
      }),
      transformResponse: (blob: Blob, meta: FetchBaseQueryMeta) => ({ data: blob, meta }),
    }),
    getFreeReport: builder.query<FreeReport[], PageableRequest<FreeReportFilter>>({
      query: (payload) => ({
        url: FREE_REPORT,
        method: 'GET',
        params: {
          ...payload?.filters,
          sort: mapSortToQueryParameter(payload?.sort),
        },
      }),
      providesTags: [reportTags.FREE],
    }),
    downloadFreeReport: builder.query<
      { data: Blob; meta: FetchBaseQueryMeta },
      PageableRequest<FreeReportFilter>
    >({
      query: (payload) => ({
        url: FREE_REPORT_DOWNLOAD,
        method: 'GET',
        params: {
          ...payload?.filters,
          sort: mapSortToQueryParameter(payload?.sort),
        },
        responseHandler: (response) => response.blob(),
      }),
      transformResponse: (blob: Blob, meta: FetchBaseQueryMeta) => ({ data: blob, meta }),
    }),
    getMortalityReport: builder.query<
      MortalityReport[],
      PageableRequest<MortalityReportFilter>
    >({
      query: (payload) => ({
        url: MORTALITY_REPORT,
        method: 'GET',
        params: {
          ...payload?.filters,
          sort: mapSortToQueryParameter(payload?.sort),
        },
      }),
      providesTags: [reportTags.MORTALITY],
    }),
    downloadMortalityReport: builder.query<
      { data: Blob; meta: FetchBaseQueryMeta },
      PageableRequest<MortalityReportFilter>
    >({
      query: (payload) => ({
        url: MORTALITY_REPORT_DOWNLOAD,
        method: 'GET',
        params: {
          ...payload?.filters,
          sort: mapSortToQueryParameter(payload?.sort),
        },
        responseHandler: (response) => response.blob(),
      }),
      transformResponse: (blob: Blob, meta: FetchBaseQueryMeta) => ({ data: blob, meta }),
    }),
    exportTask: builder.query<
      { data: Blob; meta: FetchBaseQueryMeta },
      TaskIndividualReportPayload
    >({
      query: (payload) => ({
        url: TASK_INDIV_DOWNLOAD,
        method: 'GET',
        params: {
          ...payload?.filters,
          sort: mapSortToQueryParameter(payload?.sort),
        },
        responseHandler: (response) => response.blob(),
      }),
      transformResponse: (blob: Blob, meta: FetchBaseQueryMeta) => ({ data: blob, meta }),
    }),
    downloadTaskArchiveReport: builder.query<
      { data: Blob; meta: FetchBaseQueryMeta },
      TaskIndividualReportPayload
    >({
      query: (payload) => ({
        url: TASK_INDIV_ARCHIVED_DOWNLOAD,
        method: 'GET',
        params: {
          ...payload?.filters,
          sort: mapSortToQueryParameter(payload?.sort),
        },
        responseHandler: (response) => response.blob(),
      }),
      transformResponse: (blob: Blob, meta: FetchBaseQueryMeta) => ({ data: blob, meta }),
    }),
    downloadControlReport: builder.query<
      { data: Blob; meta: FetchBaseQueryMeta },
      TaskIndividualReportPayload
    >({
      query: (payload) => ({
        url: TASK_CONTROL_REPORT,
        method: 'GET',
        params: {
          ...payload?.filters,
          sort: mapSortToQueryParameter(payload?.sort),
        },
        responseHandler: (response) => response.blob(),
      }),
      transformResponse: (blob: Blob, meta: FetchBaseQueryMeta) => ({ data: blob, meta }),
    }),
    downloadAutopsyAct: builder.query<
      { data: Blob; meta: FetchBaseQueryMeta },
      { actId: string }
    >({
      query: ({ actId }) => ({
        url: AUTOPSY_ACT_DOWNLOAD(actId),
        method: 'GET',
        params: { format: 'PDF' },
        responseHandler: (response) => response.blob(),
      }),
      transformResponse: (blob: Blob, meta: FetchBaseQueryMeta) => ({ data: blob, meta }),
    }),
  }),
});

export const {
  useLazyGetFreeReportQuery,
  useLazyGetTaskReportQuery,
  useLazyDownloadTaskReportQuery,
  useLazyDownloadFreeReportQuery,
  useLazyGetMortalityReportQuery,
  useLazyDownloadMortalityReportQuery,
  useLazyExportTaskQuery,
  useLazyDownloadTaskArchiveReportQuery,
  useLazyDownloadControlReportQuery,
  useLazyDownloadAutopsyActQuery,
} = reportApi;
